<div class="trigger-editor">
  <p class="callout">
    Run this agent on its own when an application's event happens (an Alert Manager event master with Agent ticked,
    on activity or on a schedule). The agent receives a trigger payload: the event's details, the variables you pick
    and your instructions. A facility sends the event once its copy (Alert Copy) has Agent ticked. Each account runs
    its own copy of the published agent; triggers go live when the agent is published.
  </p>

  @if (loading) {
    <p class="field-note">Loading triggers…</p>
  } @else if (loadError) {
    <p class="error-banner">{{ loadError }}</p>
  } @else {
    @if (!definitions.length) {
      <p class="info-note">No events have Agent on. Tick Agent and choose the Application on a system Event Master (Alert Manager) to create a trigger.</p>
    }

    @for (row of triggers; track $index; let i = $index) {
      <section class="trigger-card">
        <header class="trigger-card__header">
          <strong>{{ row.name || 'Trigger ' + (i + 1) }}</strong>
          <span class="state-chip" [class.state-chip--on]="row.state === 'enabled'" [class.state-chip--off]="row.state === 'disabled'">
            {{ row.state === 'draft' ? 'Draft: live after publish' : row.state === 'enabled' ? 'Enabled' : 'Disabled' }}
          </span>
          @if (!readOnly) {
            <button type="button" class="text-btn text-btn--danger" (click)="removeTrigger(i)">Remove</button>
          }
        </header>

        <div class="trigger-grid">
          <div class="of-field">
            <input type="text" class="of-control" placeholder="New claim tickets" [(ngModel)]="row.name"
                   [name]="'triggerName' + i" [disabled]="readOnly" (ngModelChange)="touch(row)">
            <label>Name</label>
          </div>
          <div class="of-field">
            <select class="of-control" [(ngModel)]="row.application" [name]="'triggerApp' + i" [disabled]="readOnly"
                    (ngModelChange)="onApplicationChange(row)">
              @if (row.application && !applications.includes(row.application)) {
                <option [value]="row.application">{{ row.application }}</option>
              }
              @for (app of applications; track app) {
                <option [value]="app">{{ app }}</option>
              }
            </select>
            <label>Application*</label>
          </div>
          <div class="of-field">
            <select class="of-control" [(ngModel)]="row.eventType" [name]="'triggerEvent' + i" [disabled]="readOnly"
                    (ngModelChange)="onEventChange(row)">
              @if (row.eventType && !definitionOf(row)) {
                <option [value]="row.eventType">{{ row.eventType }} (Agent off)</option>
              }
              @for (def of eventsOf(row.application); track def.eventType) {
                <option [value]="def.eventType">{{ def.displayName }} ({{ def.eventType }}{{ def.domain ? ', ' + def.domain : '' }})</option>
              }
            </select>
            <label>When this event happens*</label>
          </div>
          @if (!readOnly && row.state !== 'draft') {
            <div class="of-field">
              <select class="of-control" [(ngModel)]="row.state" [name]="'triggerState' + i" (ngModelChange)="touch(row)">
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
              <label>State</label>
            </div>
          }
        </div>

        <div class="conditions">
          <div class="conditions__head">
            <span class="conditions__title">Variables sent to the agent</span>
          </div>
          <p class="field-note field-note--small">Only these fields of the event go to the agent; the rest of the record stays here.</p>
          <div class="field-chips">
            @for (v of row.variables; track v) {
              <span class="chip-btn">{{ v }}
                @if (!readOnly) {
                  <button type="button" class="text-btn text-btn--danger" (click)="removeVariable(row, v)" [attr.aria-label]="'Remove ' + v">✕</button>
                }
              </span>
            }
            @if (!row.variables.length) {
              <span class="field-note field-note--small">No variables: the agent gets only the event's details and your instructions.</span>
            }
          </div>
          @if (!readOnly) {
            @if (suggestionsOf(row).length) {
              <div class="field-chips">
                <span class="field-note field-note--small">From the latest event:</span>
                @for (f of suggestionsOf(row); track f) {
                  <button type="button" class="chip-btn chip-btn--meta" (click)="addVariable(row, f)">+ {{ f }}</button>
                }
              </div>
            } @else if (row.fieldsNote) {
              <p class="field-note field-note--small">{{ row.fieldsNote }}</p>
            }
            <div class="condition-row">
              <input type="text" class="of-control" placeholder="subject or customer.tier" [(ngModel)]="row.newVariable"
                     [name]="'newVariable' + i" (keydown.enter)="addVariable(row); $event.preventDefault()">
              <button type="button" class="outline-btn outline-btn--small" (click)="addVariable(row)">+ Add variable</button>
            </div>
          }
        </div>

        <div class="conditions">
          <div class="conditions__head">
            <span class="conditions__title">Only when</span>
            @if (row.conditions.length > 1) {
              <select class="of-control conditions__combinator" [(ngModel)]="row.combinator" [name]="'triggerComb' + i"
                      [disabled]="readOnly" (ngModelChange)="touch(row)">
                <option value="all">all conditions match</option>
                <option value="any">any condition matches</option>
              </select>
            }
          </div>
          @if (!row.conditions.length) {
            <p class="field-note field-note--small">No conditions: every {{ row.eventType }} event runs the agent.</p>
          }
          @for (c of row.conditions; track $index; let j = $index) {
            <div class="condition-row">
              <input type="text" class="of-control" [(ngModel)]="c.path" [name]="'condPath' + i + '_' + j" [disabled]="readOnly"
                     placeholder="data.priority" title="data.<field> of the alert's resource, or facility_id" (ngModelChange)="touch(row)">
              <select class="of-control" [(ngModel)]="c.op" [name]="'condOp' + i + '_' + j" [disabled]="readOnly" (ngModelChange)="touch(row)">
                @for (o of operators; track o.id) {
                  <option [value]="o.id">{{ o.label }}</option>
                }
              </select>
              @if (operatorOf(c.op)?.value === 'one' || operatorOf(c.op)?.value === 'list') {
                <select class="of-control" [(ngModel)]="c.type" [name]="'condType' + i + '_' + j" [disabled]="readOnly" (ngModelChange)="touch(row)">
                  @for (t of valueTypes; track t.id) {
                    <option [value]="t.id">{{ t.label }}</option>
                  }
                </select>
              } @else {
                <span></span>
              }
              @if (operatorOf(c.op)?.value !== 'none') {
                <input type="text" class="of-control" [(ngModel)]="c.value" [name]="'condValue' + i + '_' + j" [disabled]="readOnly"
                       [placeholder]="operatorOf(c.op)?.value === 'list' ? 'high, urgent' : operatorOf(c.op)?.value === 'number' ? '3' : 'value'"
                       (ngModelChange)="touch(row)">
              } @else {
                <span></span>
              }
              @if (!readOnly) {
                <button type="button" class="text-btn text-btn--danger" (click)="removeCondition(row, j)" aria-label="Remove condition">✕</button>
              }
            </div>
          }
          @if (!readOnly) {
            <button type="button" class="outline-btn outline-btn--small" (click)="addCondition(row)">+ Add condition</button>
          }
        </div>

        <div class="of-field of-field--area">
          <textarea class="of-control" rows="4" [(ngModel)]="row.messageTemplate" [name]="'triggerTemplate' + i"
                    [disabled]="readOnly" (ngModelChange)="touch(row)"></textarea>
          <label>Instructions sent with the event*</label>
        </div>
        @if (!readOnly) {
          <div class="field-chips">
            <span class="field-note field-note--small">Insert:</span>
            @for (v of row.variables; track v) {
              <button type="button" class="chip-btn" (click)="insertField(row, 'data.' + v)">{{ v }}</button>
            }
            @for (f of envelopeFields; track f) {
              <button type="button" class="chip-btn chip-btn--meta" (click)="insertField(row, f)">{{ f }}</button>
            }
          </div>
          <p class="field-note field-note--small">A variable goes in as <code ngNonBindable>{{data.subject}}</code>; the payload also lists every variable for the agent.</p>
        }

        <div class="trigger-grid">
          <div class="of-field">
            <input type="number" class="of-control" min="0" max="5" [(ngModel)]="row.maxRetries" [name]="'triggerRetries' + i"
                   [disabled]="readOnly" (ngModelChange)="touch(row)">
            <label>Retries when a run fails</label>
          </div>
          @if (row.state === 'draft') {
            <div class="form-check form-switch toggle-row">
              <input class="form-check-input" type="checkbox" role="switch" [id]="'triggerOnPublish' + i"
                     [(ngModel)]="row.enabledOnPublish" [name]="'triggerOnPublish' + i" [disabled]="readOnly" (ngModelChange)="touch(row)">
              <label class="form-check-label" [for]="'triggerOnPublish' + i">Enable when the agent is published</label>
            </div>
          }
        </div>

        @if (!readOnly) {
          <div class="trigger-test">
            <button type="button" class="outline-btn outline-btn--small" [disabled]="row.testing || !row.eventType" (click)="runTest(row)">
              {{ row.testing ? 'Testing…' : 'Try with the latest event' }}
            </button>
            @if (row.test) {
              <div class="trigger-test__result" [class.trigger-test__result--miss]="!row.test.matched">
                <strong>{{ row.test.matched ? 'Matches: the agent would receive' : 'Does not match: the agent would not run' }}</strong>
                @if (row.test.matched) {
                  <pre>{{ row.test.message }}</pre>
                } @else if (row.test.message && !row.test.sample) {
                  <span class="field-note field-note--small">{{ row.test.message }}</span>
                }
              </div>
            }
          </div>
        }
      </section>
    }

    @if (!readOnly && definitions.length) {
      <button type="button" class="outline-btn" (click)="addTrigger()">+ Add trigger</button>
    }
    @if (readOnly && !triggers.length) {
      <p class="field-note">This agent has no triggers.</p>
    }
  }
</div>
